import { If } from '@finn/design-system/atoms/If';
import { Textarea } from '@finn/design-system/atoms/textarea';
import { WarningFilled } from '@finn/design-system/icons/warning-filled';
import { InfoToolTip } from '@finn/ui-components';
import omit from 'lodash/omit';
import { ForwardedRef, forwardRef } from 'react';
import { ControllerProps, useFormContext } from 'react-hook-form';

import { useTextWithValues } from '../../../hooks/useValue/useTextWithValues';
import { Field } from '../types';
import { FormDescription, FormItem, FormLabel } from './form';

type TextareaFieldProps = Field &
  Omit<Parameters<ControllerProps['render']>['0']['field'], 'ref'>;

const DescriptionItem = ({ children }: { children: string }) => {
  const [description] = useTextWithValues(children);

  return (
    <FormDescription className="body-14-regular">{description}</FormDescription>
  );
};

export const TextareaField = forwardRef(
  (
    { ...field }: TextareaFieldProps,
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const { formState } = useFormContext();
    if (
      field.type !== 'text' &&
      field.type !== 'number' &&
      field.type !== 'password' &&
      field.type !== 'textarea'
    ) {
      return null;
    }

    const error = formState.errors?.[field.name];

    return (
      <FormItem className={field.className}>
        <If condition={Boolean(field.title)}>
          <FormLabel className="inline-flex gap-1 body-14-regular">
            {field.title}
            {field.tooltip?.label ? (
              <InfoToolTip content={field.tooltip?.label} />
            ) : null}
          </FormLabel>
        </If>
        <Textarea
          ref={ref}
          label={field.label}
          error={Boolean(error?.message)}
          textAreaClassName="min-h-44"
          {...omit(field, 'visibilityConfig')}
        />
        <If condition={Boolean(error?.message)}>
          <div className="!mt-4 flex items-center gap-2.5 rounded-sm bg-lightRed p-4 body-16-light">
            <WarningFilled />
            <span>{error?.message}</span>
          </div>
        </If>
        {field.descriptions?.map((description) => (
          <DescriptionItem key={description}>{description}</DescriptionItem>
        ))}
      </FormItem>
    );
  }
);
